// Zone-grouping logic shared between the Table view (LaneTable.tsx) and
// the Floor view (LaneFloor.tsx). Both views render the same lanes
// grouped by `zone` with the same sort order — keeping the grouping in
// one place stops the two views drifting apart.

import { statusCategory } from "./laneStatusConfig";
import type { Lane } from "./useLanes";

export const ZONE_NULL = "__no_zone__";

export interface ZoneGroup {
    zone: string;            // ZONE_NULL when the underlying value was null
    lanes: Lane[];
    busy: number;
    idle: number;
    unprocessed: number;
}

export const buildZoneGroups = (lanes: Lane[]): ZoneGroup[] => {
    const map = new Map<string, ZoneGroup>();
    for (const l of lanes) {
        const key = l.zone ?? ZONE_NULL;
        let g = map.get(key);
        if (!g) {
            g = { zone: key, lanes: [], busy: 0, idle: 0, unprocessed: 0 };
            map.set(key, g);
        }
        g.lanes.push(l);
        // Tally by the status's (admin-overridable) KPI category so the
        // zone summary stays consistent with the KPI strip.
        const cat = statusCategory(l.status);
        if (cat === "idle") g.idle++;
        else if (cat === "unprocessed") g.unprocessed++;
        else if (cat === "auction" || cat === "outbound" || cat === "stock") g.busy++;
    }
    const groups = Array.from(map.values());
    groups.forEach(g => {
        g.lanes.sort((a, b) => {
            const aw = a.walk_order;
            const bw = b.walk_order;
            if (aw == null && bw == null) return a.barcode.localeCompare(b.barcode);
            if (aw == null) return 1;
            if (bw == null) return -1;
            if (aw !== bw) return aw - bw;
            return a.barcode.localeCompare(b.barcode);
        });
    });
    groups.sort((a, b) => {
        if (a.zone === ZONE_NULL) return 1;
        if (b.zone === ZONE_NULL) return -1;
        return a.zone.localeCompare(b.zone);
    });
    return groups;
};

export const zoneDisplay = (zone: string): string =>
    zone === ZONE_NULL ? "—" : zone;

// Short label used in the Floor view's left rail — just the letter(s)
// after a "Zone " prefix if the source data carries one ("Zone F" → "F").
// Falls back to the full string when there's no such prefix.
export const zoneShortLabel = (zone: string): string => {
    if (zone === ZONE_NULL) return "—";
    const m = zone.match(/^zone\s+(.+)$/i);
    return m ? m[1] : zone;
};
